import { type PropsWithChildren } from "react"
import { Container, Stack, Stepper, type StepperProps } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { GdprPrivacyPolicy } from "@/components/atoms"
import CustomHead from "@/components/atoms/CustomHead"
import { ErrorBoundary } from "@/components/molecules/ErrorBoundary"
import { AppLayout } from "@/features/navigation"
import { type Partner } from "@/features/partners"
import { type OnboardingRouteNames } from "@/routes/onboarding"
import { StickyReinsurances } from "../reinsurances/components/StickyReinsurances"
import { SubscriptionTrustpilot } from "./SubscriptionTrustpilot"

type SubscriptionLayoutProps = PropsWithChildren<{
    withStickyReinsurance?: boolean
    stepName: OnboardingRouteNames
    stepperProps: StepperProps
    withStepper?: boolean
    withPrivacyPolicy?: boolean
    withTrustpilot?: boolean
    isNavless?: boolean
    partner?: Partner | null
    isHeadless?: boolean
    href?: string | null
}>

export function SubscriptionLayout({
    href = null,
    withPrivacyPolicy = true,
    withTrustpilot = false,
    withStepper,
    withStickyReinsurance = false,
    stepName,
    stepperProps,
    partner = null,
    isHeadless = false,
    children,
}: SubscriptionLayoutProps) {
    const t = useTranslations("SubscriptionLayout")

    return (
        <AppLayout
            partner={partner}
            isNavless
            href={href}
            isHeadless={isHeadless}
        >
            <CustomHead
                robots="noindex,nofollow"
                title={t("head.title")}
                description={t("head.description")}
            />
            <Container
                maxWidthPreset="full"
                padding="none"
                theme="base500"
                attributes={{ "data-testid": `${stepName}` }}
            >
                {withStickyReinsurance && <StickyReinsurances stepName={stepName} />}
                <Container
                    fullWidth
                    maxWidth={800}
                    marginX={{ base: "none", md: "auto" }}
                    paddingY="lg"
                >
                    <Stack
                        fullWidth
                        flexDirection="column"
                        gap="xl"
                    >
                        {/* TODO display banner with referrer name only when a referrer exist */}
                        {withStepper && <Stepper {...stepperProps} />}
                        <ErrorBoundary>{children}</ErrorBoundary>
                    </Stack>
                </Container>
            </Container>
            {withTrustpilot && <SubscriptionTrustpilot />}
            {withPrivacyPolicy && <GdprPrivacyPolicy />}
        </AppLayout>
    )
}

SubscriptionLayout.messages = [
    "SubscriptionLayout",
    ...AppLayout.messages,
    ...ErrorBoundary.messages,
    ...SubscriptionTrustpilot.messages,
] as const
